import { svg } from 'components/icons/svg';
import { Tooltip } from 'components/ui/tooltip';
import { cn } from 'utils/cn';

export interface StatusIconProps {
    tooltipText?: string;
    type: 'success' | 'error' | 'info' | 'warning';
}

// Drawn at their natural size rather than the 1em square the vendored glyphs take.
const Checkmark = svg(
    '0 0 11 10',
    <path d="M3.88407 9.14604C3.75073 9.14604 3.6174 9.11826 3.48407 9.0627C3.35073 9.00715 3.22851 8.92382 3.1174 8.8127L0.300734 5.99604C0.0896234 5.78493 -0.0131544 5.52659 -0.00759882 5.22104C-0.00204327 4.91548 0.10629 4.65715 0.317401 4.44604C0.528512 4.23493 0.789623 4.12937 1.10073 4.12937C1.41185 4.12937 1.67296 4.23493 1.88407 4.44604L3.9174 6.47937L9.13407 1.2627C9.34518 1.05159 9.59796 0.943259 9.8924 0.937703C10.1868 0.932148 10.4452 1.04048 10.6674 1.2627C10.8785 1.47381 10.9841 1.72937 10.9841 2.02937C10.9841 2.32937 10.8785 2.58493 10.6674 2.79604L4.65073 8.8127C4.53962 8.92382 4.4174 9.00715 4.28407 9.0627C4.15073 9.11826 4.0174 9.14604 3.88407 9.14604Z" />,
    { className: 'h-[10px] w-[11px]' }
);

const ExclamationMark = svg(
    '0 0 3 12',
    <path d="M1.4952 11.7306C1.09524 11.7306 0.753866 11.5881 0.471075 11.3033C0.188275 11.0185 0.046875 10.6761 0.046875 10.2761C0.046875 9.87618 0.189286 9.5348 0.474109 9.25201C0.758941 8.96922 1.10134 8.82782 1.5013 8.82782C1.90126 8.82782 2.24264 8.97023 2.52543 9.25505C2.80823 9.53988 2.94963 9.88227 2.94963 10.2822C2.94963 10.6822 2.80721 11.0236 2.52238 11.3064C2.23756 11.5892 1.89517 11.7306 1.4952 11.7306ZM1.49754 7.71949C1.13449 7.71949 0.823934 7.59029 0.565867 7.33189C0.3078 7.07349 0.178767 6.76286 0.178767 6.4V1.72572C0.178767 1.36287 0.308039 1.05224 0.566582 0.793846C0.825115 0.535449 1.13591 0.40625 1.49897 0.40625C1.86201 0.40625 2.17257 0.535449 2.43064 0.793846C2.6887 1.05224 2.81774 1.36287 2.81774 1.72572V6.4C2.81774 6.76286 2.68847 7.07349 2.42992 7.33189C2.17139 7.59029 1.86059 7.71949 1.49754 7.71949Z" />,
    { className: 'h-[12px] w-[3px]' }
);

const InfoMark = svg(
    '0 0 3 14',
    <path d="M1.49779 2.96124C1.09547 2.96124 0.75339 2.81998 0.471565 2.53747C0.189741 2.25496 0.0488281 1.91253 0.0488281 1.51021C0.0488281 1.10604 0.190086 0.762411 0.4726 0.479322C0.755 0.196232 1.09742 0.0546875 1.49986 0.0546875C1.90219 0.0546875 2.24427 0.196577 2.52609 0.480356C2.80792 0.764251 2.94883 1.10828 2.94883 1.51245C2.94883 1.91673 2.80757 2.25927 2.52506 2.54006C2.24254 2.82085 1.90012 2.96124 1.49779 2.96124ZM1.49745 13.0253C1.1901 13.0253 0.929315 12.9179 0.7151 12.703C0.500886 12.4881 0.393779 12.2271 0.393779 11.9201V5.67618C0.393779 5.36917 0.501346 5.10821 0.71648 4.89331C0.931615 4.67841 1.19286 4.57095 1.50021 4.57095C1.80756 4.57095 2.06834 4.67841 2.28256 4.89331C2.49677 5.10821 2.60388 5.36917 2.60388 5.67618V11.9201C2.60388 12.2271 2.49631 12.4881 2.28118 12.703C2.06604 12.9179 1.8048 13.0253 1.49745 13.0253Z" />,
    { className: 'h-[14px] w-[3px]' }
);

/** Glyph and tile colours per status. */
const TYPE = {
    success: { Glyph: Checkmark, className: 'text-success bg-success-light' },
    error: { Glyph: ExclamationMark, className: 'text-error bg-error-light' },
    info: { Glyph: InfoMark, className: 'text-link bg-info-lighter' },
    warning: { Glyph: ExclamationMark, className: 'text-warning bg-warning-light' },
} as const;

export default function StatusIcon({ tooltipText, type }: StatusIconProps) {
    const { Glyph, className } = TYPE[type];

    // The tooltip names nothing for assistive tech, so the status carries its text as the tile's name.
    const boxedIcon = (
        <span
            role={tooltipText ? 'img' : undefined}
            aria-label={tooltipText}
            className={cn('inline-flex size-6 items-center justify-center rounded-sm', className)}
        >
            <Glyph />
        </span>
    );

    if (!tooltipText) {
        return boxedIcon;
    }

    return <Tooltip content={tooltipText}>{boxedIcon}</Tooltip>;
}
